import type {
  ActiveDepositResponse,
  CreateCustomerWithdrawalRequest,
  CustomerActivityList,
  CustomerBalance,
  CustomerWithdrawal,
  CustomerWithdrawalList,
  ReserveDepositRequest,
} from '@pf/bff-contract';
import type { TransactionDetail } from '@pf/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-client';
import { cashierRequest } from './http';

/**
 * TanStack Query bindings for the participating website's BFF.
 *
 * These are the *customer* area's only data source (SAD P9 / D-I7), and every one of them goes
 * through {@link cashierRequest}, which resolves the verb, the path and the response schema from
 * `@pf/bff-contract`'s manifest. Nothing in this file names a URL. That is what makes the console
 * a *default* UI rather than a required one: swap the BFF for any other implementation of the
 * Cashier Profile and these hooks keep working, because they were never coupled to
 * `apps/mock-website` in the first place — only to the profile it happens to implement.
 *
 * The polling here is what makes the deposit screen feel live: while a transaction is in a
 * non-terminal state the console re-reads it every couple of seconds, which is exactly the
 * reconciliation posture the SAD prescribes for a webhook-driven system — the webhook is a
 * notification, the read is the truth.
 */

/** Deposit states in which the console keeps polling for a change. */
const LIVE_DEPOSIT_STATES = new Set([
  'RESERVED',
  'AWAITING_EVIDENCE',
  'VERIFYING',
  // A deposit sitting in manual review is still in flight — an operator will move it to
  // CONFIRMED/COMPLETED or REJECTED. Without this the page freezes on "Under review" and never
  // reflects the operator's decision until the customer manually refreshes.
  'UNDER_REVIEW',
  'CONFIRMED',
  'CREDIT_PENDING',
]);

/** The signed-in customer's balances, from the website's own ledger. */
export function useBalance(): UseQueryResult<CustomerBalance> {
  return useQuery({
    queryKey: queryKeys.bff.balance,
    queryFn: ({ signal }) => cashierRequest('getBalance', { signal }),
  });
}

/**
 * The order book: amounts and availability only.
 *
 * Refreshed on a short interval because depth genuinely changes underneath the depositor — an
 * amount that was available ten seconds ago may have been claimed by someone else, and the
 * screen should say so before the reserve attempt fails.
 */
export function useDepositOptions() {
  return useQuery({
    queryKey: queryKeys.bff.depositOptions,
    queryFn: ({ signal }) => cashierRequest('listDepositOptions', { signal }),
    refetchInterval: 10_000,
  });
}

/**
 * Reserve one withdrawal at an exact amount. `409 OUT_OF_STOCK` when the bucket is empty.
 *
 * The body is the amount and nothing else. The client reference PaymentFlow idempotency-keys the
 * reservation on, and the depositor it is booked against, are both the BFF's to supply from the
 * session — a browser that could choose either could replay another customer's claim.
 */
export function useReserveDeposit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (amount: ReserveDepositRequest['amount']) =>
      cashierRequest('reserveDeposit', { body: { amount } satisfies ReserveDepositRequest }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.bff.depositOptions });
      await client.invalidateQueries({ queryKey: queryKeys.bff.activeDeposit });
    },
  });
}

/**
 * The customer's in-flight deposit, if any — the resume path.
 *
 * Reserving claims one withdrawal exclusively and takes it out of the order book, and the payee is
 * disclosed exactly once in the reserve response (SAD §5). Holding that response in component
 * state alone meant a refresh lost the reservation entirely: the claim stayed live, the amount was
 * gone from the book, and the screen had no way back to it. This is how the screen gets back.
 *
 * Polled on the same cadence as the deposit itself, so a reservation that expires in another tab
 * stops being offered here too.
 */
export function useActiveDeposit(): UseQueryResult<ActiveDepositResponse> {
  return useQuery({
    queryKey: queryKeys.bff.activeDeposit,
    queryFn: ({ signal }) => cashierRequest('getActiveDeposit', { signal }),
    refetchInterval: 15_000,
  });
}

/** A deposit's live state. Polls while the transaction is still in flight. */
export function useDeposit(id: string): UseQueryResult<TransactionDetail> {
  return useQuery({
    queryKey: queryKeys.bff.deposit(id),
    queryFn: ({ signal }) => cashierRequest('getDeposit', { params: { id }, signal }),
    enabled: id !== '',
    refetchInterval: (query) =>
      LIVE_DEPOSIT_STATES.has(query.state.data?.status ?? '') ? 2_500 : false,
  });
}

/** Tell the website the transfer has been made: RESERVED → AWAITING_EVIDENCE. */
export function useMarkPaid(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => cashierRequest('markDepositPaid', { params: { id }, body: {} }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.bff.deposit(id) }),
  });
}

/** Give up a reservation before paying: RESERVED → EXPIRED, slot released. */
export function useReleaseReservation(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => cashierRequest('releaseDeposit', { params: { id }, body: {} }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.bff.deposit(id) });
      await client.invalidateQueries({ queryKey: queryKeys.bff.depositOptions });
      await client.invalidateQueries({ queryKey: queryKeys.bff.activeDeposit });
    },
  });
}

/**
 * PUT the slip straight to object storage, and *insist it landed*.
 *
 * Two bugs hid behind the old one-line `await fetch(...)` with no result check:
 *
 *  1. **A failed upload was silent.** The evidence reference was submitted regardless, so a slip
 *     that never stored still triggered verification. Against the mock provider — which reads a QR
 *     payload, not the image — that happens to pass; against a real provider it fails downstream
 *     with an error that names verification, not the upload that actually broke.
 *  2. **A transient 5xx killed the whole deposit.** Object stores return `503 SlowDown`, and a
 *     freshly-started one (the dev stack's MinIO) returns 503 during its init window even after
 *     liveness passes. One unlucky PUT and the depositor was stuck.
 *
 * So: check the response, and retry a 5xx a couple of times with backoff before giving up. A 4xx
 * is not retried — a rejected content type or an expired URL will not fix itself.
 */
async function putSlipToStorage(url: string, contentType: string, file: File): Promise<void> {
  const attempts = 3;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    let status = 0;
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (response.ok) return;
      status = response.status;
    } catch {
      // A network-level failure (TypeError "Failed to fetch") — treat like a 5xx and retry.
      status = 0;
    }
    // A 4xx is a verdict, not weather: a wrong content type or a lapsed URL will not self-heal.
    if (status >= 400 && status < 500) {
      throw new Error(`The slip upload was refused (${String(status)}). Choose the file again.`);
    }
    if (attempt === attempts) {
      throw new Error('The slip could not be uploaded. Check your connection and try again.');
    }
    // 250ms, 500ms — enough to clear a store's startup or a momentary SlowDown.
    await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
  }
}

/**
 * The two-step evidence upload (SAD §8): mint a short-TTL pre-signed URL, PUT the file straight
 * to object storage, then submit the reference — the slip image never passes through the API.
 *
 * The PUT is the one raw `fetch` in the customer area, and legitimately so: its target is an
 * object-storage URL the BFF just minted, not a profile endpoint, and it carries image bytes
 * rather than JSON. It goes through {@link putSlipToStorage}, which is where "the upload actually
 * succeeded" stops being assumed.
 */
export function useSubmitEvidence(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const contentType = file.type === '' ? 'image/jpeg' : file.type;
      const upload = await cashierRequest('createEvidenceUpload', {
        body: { kind: 'BANK_SLIP', content_type: contentType },
      });
      await putSlipToStorage(upload.upload_url, contentType, file);
      return cashierRequest('submitDepositEvidence', {
        params: { id },
        body: { kind: 'BANK_SLIP', evidence_id: upload.evidence_id },
      });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.bff.deposit(id) }),
  });
}

/** The customer's own withdrawal requests, with their position in the pool. */
export function useCustomerWithdrawals(): UseQueryResult<CustomerWithdrawalList> {
  return useQuery({
    queryKey: queryKeys.bff.withdrawals,
    queryFn: ({ signal }) => cashierRequest('listWithdrawals', { signal }),
    refetchInterval: 15_000,
  });
}

/** Request a withdrawal: the website locks the funds and mints the `lock_reference` first. */
export function useCreateWithdrawal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerWithdrawalRequest) =>
      cashierRequest('createWithdrawal', { body: input }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.bff.withdrawals });
      await client.invalidateQueries({ queryKey: queryKeys.bff.balance });
    },
  });
}

/** Cancel an available withdrawal, releasing the funds lock. */
export function useCancelWithdrawal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cashierRequest('cancelWithdrawal', { params: { id }, body: {} }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.bff.withdrawals });
      await client.invalidateQueries({ queryKey: queryKeys.bff.balance });
    },
  });
}

/** Filters the Transaction History page keeps in the URL. */
export interface ActivityFilters {
  /** `ALL`, `DEPOSIT` or `WITHDRAWAL`. */
  kind: string;
  /** A deposit or withdrawal status, or `ALL`. */
  status: string;
  /** Free-text search over ids and references. */
  q: string;
}

/** The customer's combined deposit and withdrawal history. */
export function useActivity(filters: ActivityFilters): UseQueryResult<CustomerActivityList> {
  return useQuery({
    queryKey: queryKeys.bff.activity({ ...filters }),
    queryFn: ({ signal }) => cashierRequest('listActivity', { signal, query: { ...filters } }),
  });
}

/** A single withdrawal, for the history page's detail panel. */
export function useCustomerWithdrawal(id: string): UseQueryResult<CustomerWithdrawal> {
  return useQuery({
    queryKey: queryKeys.bff.withdrawal(id),
    queryFn: ({ signal }) => cashierRequest('getActivityWithdrawal', { params: { id }, signal }),
    enabled: id !== '',
  });
}
